/**
 * Behavior Tools
 *
 * AI agent tools for managing behaviors on scene objects:
 * add_behavior, remove_behavior, get_behavior, commit_behavior (each also over `entries`), list_behaviors
 * and the wire tools are trace-viewer's (`shared-scene-tools.ts`); attach_script,
 * actions, state previews, wire_group_state, simulate_select, bindings, runtime
 * probes and play mode are this editor's.
 */

import type { ToolDefinition } from './types';
import type { ToolContext } from '../agent-helpers';
import { resolveObject } from '../agent-helpers';
import type { ActionSchema } from 'trace-viewer/behaviors';
import { getBehaviorSchema } from 'trace-viewer/behaviors';
import { getUndoManager, AddBehaviorCommand, pickabilityWarning, wireInteractions } from 'trace-viewer/editor';
import type { Interaction } from 'trace-viewer/editor';
import { resolveStateOwner, applyStateToSubtree, collectScopedStateTargets } from 'trace-viewer/editor';
import { Properties } from 'trace-viewer/behaviors';
import { PointerInfo, PointerEventTypes } from '@babylonjs/core/Events/pointerEvents';
import { PickingInfo } from '@babylonjs/core/Collisions/pickingInfo';
import { dispatchBehaviorChanged } from '../../editor-events';
import { enterPlayMode, exitPlayMode } from '../../PlayModeSnapshot';
import { authoringPorts, oneWiringAtATime, reportOrphaned, wiringRefusalText } from './authoring-ops';
import { probeRuntime, inspectBindings, findStaticReadouts } from './runtime-probe';
import { probeRuntimeSequence } from './runtime-sequence';
import { compactRuntimeReport } from './runtime-report';
import { configureInspectorTool } from './configure-inspector';
import { sharedSceneTool } from './shared-scene-tools';

// ── attach_script ──────────────────────────────────────────

async function executeAttachScript(
    input: Record<string, unknown>,
    ctx: ToolContext,
): Promise<string> {
    const scriptName = input.script_name as string;
    const objectRef = input.object as string;
    const overrides = input.overrides as Record<string, unknown> | undefined;
    const { traceScene } = ctx;

    // Verify script exists
    const factory = traceScene.scriptRegistry.getFactory(scriptName);
    if (!factory) {
        const available = Object.keys(traceScene.scriptsMap ?? {});
        return `Error: Script "${scriptName}" not found. Available scripts: ${available.join(', ') || '(none)'}`;
    }

    const target = resolveObject(objectRef, ctx);
    if (!target) {
        return `Error: Object "${objectRef}" not found.`;
    }

    // Check for duplicate
    if (target.getBehavior(scriptName)) {
        return `Error: "${target.name}" already has a "${scriptName}" behavior.`;
    }

    const sb = await target.addBehavior(scriptName, overrides);

    // Record undo
    if (!getUndoManager().isUndoRedoing) {
        getUndoManager().push(new AddBehaviorCommand(
            target,
            scriptName,
            sb.getData(),
        ));
    }

    // Notify UI
    dispatchBehaviorChanged({
        objectPath: target.getPath(),
        behaviorType: scriptName,
        action: 'added',
    });

    return `Attached script "${scriptName}" to "${target.name}".`;
}

// ── list_actions ──────────────────────────────────────────

/**
 * Collect all callable actions from an object's behaviors using @action schema metadata.
 */
function discoverActions(
    obj: import('trace-viewer/scene').SceneObject,
): { behaviorType: string; action: ActionSchema }[] {
    const results: { behaviorType: string; action: ActionSchema }[] = [];
    for (const behavior of obj.getBehaviors()) {
        const schema = getBehaviorSchema(behavior.name);
        if (!schema?.actions) continue;
        for (const action of schema.actions) {
            results.push({ behaviorType: behavior.name, action });
        }
    }
    return results;
}

function executeListActions(
    input: Record<string, unknown>,
    ctx: ToolContext,
): string {
    const objectRef = input.object as string;

    const target = resolveObject(objectRef, ctx);
    if (!target) {
        return `Error: Object "${objectRef}" not found.`;
    }

    const actions = discoverActions(target);
    if (actions.length === 0) {
        return `No actions available on "${target.name}". Actions are exposed by behaviors like Pages (next, prev) and Keyframes (goTo).`;
    }

    const lines: string[] = [`Actions on "${target.name}":\n`];
    for (const { behaviorType, action } of actions) {
        lines.push(`  ${behaviorType}.${action.name} — ${action.label}`);
    }
    return lines.join('\n');
}

// ── call_action ───────────────────────────────────────────

async function executeCallAction(
    input: Record<string, unknown>,
    ctx: ToolContext,
): Promise<string> {
    const objectRef = input.object as string;
    const actionName = input.action as string;
    const args = input.args as unknown[] | undefined;

    const target = resolveObject(objectRef, ctx);
    if (!target) {
        return `Error: Object "${objectRef}" not found.`;
    }

    // Without behavior_type, the one behavior on the object that declares the action.
    let behaviorType = input.behavior_type as string | undefined;
    if (!behaviorType) {
        const owners = discoverActions(target).filter(a => a.action.name === actionName).map(a => a.behaviorType);
        if (owners.length !== 1) {
            const listed = discoverActions(target).map(a => `${a.behaviorType}.${a.action.name}`);
            return `Error: behavior_type is required${owners.length > 1 ? ` — "${actionName}" is on ${owners.join(' and ')}` : ''}. Actions on "${target.name}": ${listed.join(', ') || '(none)'}`;
        }
        behaviorType = owners[0];
    }

    const behavior = target.getBehavior(behaviorType);
    if (!behavior) {
        const available = target.getBehaviors().map(b => b.name);
        return `Error: No "${behaviorType}" behavior on "${target.name}". Behaviors: ${available.join(', ') || '(none)'}`;
    }

    // Verify the action is declared in the schema
    const schema = getBehaviorSchema(behaviorType);
    const actionDef = schema?.actions?.find(a => a.name === actionName);
    if (!actionDef) {
        const available = schema?.actions?.map(a => a.name) ?? [];
        return `Error: No action "${actionName}" on "${behaviorType}". Available actions: ${available.join(', ') || '(none)'}`;
    }

    // Call the method on the behavior instance
    const method = (behavior as unknown as Record<string, unknown>)[actionName];
    if (typeof method !== 'function') {
        return `Error: Action "${actionName}" is declared but not implemented on "${behaviorType}".`;
    }

    try {
        await (method as (...a: unknown[]) => unknown).apply(behavior, args ?? []);
        return `Executed ${behaviorType}.${actionName}(${(args ?? []).map(a => JSON.stringify(a)).join(', ')}) on "${target.name}".`;
    } catch (err) {
        const msg = err instanceof Error ? err.message : String(err);
        return `Error executing action: ${msg}`;
    }
}

// ── set_group_state ────────────────────────────────────────

/**
 * Switch a state-group to a named state at runtime — a live preview. Resolves the
 * group OWNER from any member, then applies the switch across the owner + its
 * group descendants (pruned at nested boundaries) via `applyStateToSubtree`,
 * animating the authored per-state transitions. This is the exact scope a wired
 * `trace/setState` resolves at runtime (STATE_SCOPE_SPEC.md) — the runtime setState
 * op is itself scoped, so this previews precisely what a wired switch will do.
 */
function executeSetGroupState(
    input: Record<string, unknown>,
    ctx: ToolContext,
): string {
    const objectRef = input.object as string;
    const stateRaw = input.state as string | undefined;
    const animate = input.animate !== false; // default true — mirrors authored transition

    const target = resolveObject(objectRef, ctx);
    if (!target) {
        return `Error: Object "${objectRef}" not found.`;
    }

    // Climb to the boundary owner so callers can pass any member of the group.
    const owner = resolveStateOwner(target);
    const ownerProps = owner.getBehavior(Properties);
    if (!ownerProps) {
        return `Error: "${owner.name}" (resolved owner of "${target.name}") has no Properties behavior, so it owns no states.`;
    }

    // Base = clear the state (undefined). Accept "", "base", "Base" as Base.
    const isBase = stateRaw == null || stateRaw === '' ||
        stateRaw.toLowerCase() === 'base';
    const stateName = isBase ? undefined : stateRaw;

    const stateNames = ownerProps.getStateNames();
    if (stateName !== undefined && !stateNames.includes(stateName)) {
        return `Error: "${owner.name}" has no state "${stateName}". ` +
            `Available states: ${stateNames.join(', ') || '(none)'} (or "Base" to clear).`;
    }

    // Preview the same scope the runtime setState walk resolves (owner + every
    // participating descendant, nested boundaries pruned — STATE_SCOPE_SPEC.md).
    // Feed that exact set to the apply so the mutated objects and the reported
    // `members` can't diverge (applyStateToSubtree re-filters by declared state
    // anyway; members[0] is the owner, which apply() also handles — idempotent).
    const members = collectScopedStateTargets(owner, stateName);
    applyStateToSubtree(owner, stateName, members, animate);

    const label = stateName ?? 'Base';
    const memberNames = members.map(m => m.name);
    return `Set group state "${label}" on owner "${owner.name}" ` +
        `(${members.length} object${members.length === 1 ? '' : 's'}: ${memberNames.join(', ')})` +
        `${animate ? ' with animated transition' : ' (instant)'}.`;
}

// ── wire_group_state ───────────────────────────────────────

/**
 * Wire a button so tapping it switches a state GROUP to a named state. Emits a
 * single `onSelect → trace/setState(owner, state)`: `trace/setState` is a scoped
 * runtime op (STATE_SCOPE_SPEC.md), so it fans out to the owner's whole state
 * group — every participating descendant, pruned at nested boundaries — with no
 * per-member chain and no editor marker. This is exactly `wire_interaction` with
 * `action:'setGroupState'`, which snaps the target to its group owner first (so
 * the scope roots at the group, not a member).
 *
 * Counterpart to `set_group_state` (live preview switch): use this to WIRE a
 * persistent button, that to PREVIEW the switch now.
 */
async function executeWireGroupState(
    input: Record<string, unknown>,
    ctx: ToolContext,
): Promise<string> {
    const buttonRef = input.button as string;
    const targetRef = input.target as string;

    const button = resolveObject(buttonRef, ctx);
    if (!button) return `Error: button "${buttonRef}" not found.`;
    const target = resolveObject(targetRef, ctx);
    if (!target) return `Error: target "${targetRef}" not found.`;

    // `state` and `mode` are the tool's input, unchecked; the viewer checks them.
    const interaction: Interaction = {
        object: button,
        actions: [{ action: 'setGroupState', target, state: input.state as string | undefined }],
        mode: input.mode as Interaction['mode'],
    };
    let result;
    try {
        result = await wireInteractions([interaction], authoringPorts());
    } catch (err) {
        return `Error wiring "${button.name}": ${err instanceof Error ? err.message : String(err)}`;
    }
    if ('refused' in result) return `Error: ${wiringRefusalText(result.refused)}`;
    getUndoManager().push(result.command);
    reportOrphaned('wire_group_state', result.orphaned);

    // The summary names the owner and how many objects the switch spans.
    const [{ object, summaries: [summary], update }] = result.wired;
    return `Wired "${object.name}" → onSelect ${summary}. ` +
        `Verify with set_play_mode(true) + simulate_select("${object.name}").` +
        `\nGraph update: ${JSON.stringify(update)}`;
}

// ── wire_interaction ───────────────────────────────────────

// ── simulate_select ────────────────────────────────────────

/**
 * Fire a select (tap/click) interaction on a scene object, driving BOTH runtime
 * select paths so every kind of handler is triggered:
 *
 *  1. Babylon's `onPointerObservable` POINTERPICK on the object's mesh — this is
 *     what a BehaviorGraph `event/onSelect` (compiled to a
 *     FlowGraphMeshPickEventBlock) listens to. It does NOT go through
 *     `handlePointerEvent`, so firing only that path silently misses graphs.
 *  2. `TraceScene.handlePointerEvent('select')` — drives script `onSelect`
 *     hooks and Properties `:selected` pseudo-states.
 *
 * None of these have a callable action, so this was previously untriggerable
 * headlessly. Requires play mode (graphs/scripts are inert in edit mode).
 *
 * A tap is a down THEN up: by default we fire enter and exit so momentary
 * interactions complete. Pass `hold: true` to fire only the enter (down) edge.
 */
function executeSimulateSelect(
    input: Record<string, unknown>,
    ctx: ToolContext,
): string {
    const objectRef = input.object as string;
    const hold = input.hold === true;

    const target = resolveObject(objectRef, ctx);
    if (!target) {
        return `Error: Object "${objectRef}" not found.`;
    }

    const { traceScene } = ctx;
    const notes: string[] = [];
    // onSelect graphs / script hooks only run in play mode — warn loudly so a
    // no-op is never silently reported as success.
    if (!traceScene.isPlayMode()) {
        notes.push(
            'WARNING: scene is in EDIT mode — onSelect graphs and script hooks do NOT fire in edit mode, ' +
            'so this tap only changed editor selection. Enter play mode (set_play_mode) to trigger interactions.',
        );
    }

    // Path 1 — Babylon pointer-pick observable, for BehaviorGraph onSelect.
    // FlowGraphMeshPickEventBlock matches on the picked mesh (or a descendant),
    // so pick the object's first child mesh. Mirrors the canonical fireSelect()
    // in GraphCompiler. Only meaningful on the down edge (a pick is one event).
    const mesh = target.transform.getChildMeshes(false)[0] ?? null;
    if (mesh) {
        const pickInfo = new PickingInfo();
        pickInfo.hit = true;
        pickInfo.pickedMesh = mesh;
        const pointerInfo = new PointerInfo(
            PointerEventTypes.POINTERPICK,
            new PointerEvent('pointerdown'),
            pickInfo,
        );
        traceScene.getBabylonScene().onPointerObservable.notifyObservers(
            pointerInfo,
            PointerEventTypes.POINTERPICK,
        );
    } else {
        notes.push(
            `NOTE: "${target.name}" has no mesh, so a BehaviorGraph onSelect (mesh-pick) cannot match — ` +
            'only script onSelect and :selected handlers were driven.',
        );
    }

    // Path 2 — editor/script select path, for script onSelect + :selected states.
    traceScene.handlePointerEvent('select', target, true);
    if (!hold) {
        traceScene.handlePointerEvent('select', target, false);
    }

    const edge = hold ? 'select-down (held)' : 'tap (down+up)';
    const base = `Fired ${edge} on "${target.name}"${mesh ? '' : ' (no mesh)'}.`;

    // This drove POINTERPICK synthetically against the target's own mesh — it does
    // NOT go through real ray-picking. If the object has an onSelect graph but no
    // pickable surface of its own, a REAL tap would miss it. Surface that so a
    // green simulate_select isn't mistaken for "real taps work".
    if (target.getBehavior('BehaviorGraph') != null) {
        const pickWarn = pickabilityWarning(target);
        if (pickWarn) {
            notes.push(
                'CAVEAT: simulated activation does not verify a real pointer hit. ' + pickWarn,
            );
        }
    }

    return notes.length ? `${base}\n${notes.join('\n')}` : base;
}

// ── set_play_mode ──────────────────────────────────────────

/**
 * Enter or exit play mode. Interactions (onSelect graphs, script hooks,
 * unmanaged animations, start events) only run in play mode — so this is a
 * prerequisite for verifying wired-up interactivity via `simulate_select`.
 * Entering play mode fires start interactions; exiting stops running scripts.
 */
async function executeSetPlayMode(
    input: Record<string, unknown>,
    ctx: ToolContext,
): Promise<string> {
    const enabled = input.enabled;
    if (typeof enabled !== 'boolean') {
        return `Error: "enabled" must be a boolean (true to enter play mode, false to exit).`;
    }

    const { traceScene } = ctx;
    const was = traceScene.isPlayMode();
    if (was === enabled) {
        return `Scene is already in ${enabled ? 'play' : 'edit'} mode — no change.`;
    }

    // The toolbar's path (PlayModeSnapshot.ts): what play mode changes is undone on the way back to editing.
    if (enabled) enterPlayMode(traceScene);
    else await exitPlayMode(traceScene);
    return `Entered ${enabled ? 'PLAY' : 'EDIT'} mode.` +
        (enabled ? ' Interactions (onSelect, scripts, start events) are now live. Use probe_runtime to observe tick/Timeline motion; headless scenes advance only when explicitly stepped.' : '');
}

// ── Exports ────────────────────────────────────────────────

export const behaviorTools: ToolDefinition[] = [
    sharedSceneTool('wire_clock'),
    configureInspectorTool,
    sharedSceneTool('add_behavior'),
    sharedSceneTool('remove_behavior'),
    sharedSceneTool('get_behavior'),
    sharedSceneTool('commit_behavior'),
    {
        tool: {
            name: 'attach_script',
            input_schema: {
                type: 'object' as const,
                properties: {
                    script_name: { type: 'string' },
                    object: { type: 'string' },
                    overrides: { type: 'object' },
                },
                required: ['script_name', 'object'],
            },
        },
        execute: executeAttachScript,
    },
    sharedSceneTool('list_behaviors'),
    {
        tool: {
            name: 'list_actions',
            input_schema: {
                type: 'object' as const,
                properties: {
                    object: { type: 'string' },
                },
                required: ['object'],
            },
        },
        execute: (input, ctx) => Promise.resolve(executeListActions(input, ctx)),
    },
    {
        tool: {
            name: 'call_action',
            input_schema: {
                type: 'object' as const,
                properties: {
                    object: { type: 'string' },
                    behavior_type: { type: 'string' },
                    action: { type: 'string' },
                    args: {
                        type: 'array',
                        items: {},
                    },
                },
                required: ['object', 'action'],
            },
        },
        execute: executeCallAction,
    },
    {
        tool: {
            name: 'set_group_state',
            input_schema: {
                type: 'object' as const,
                properties: {
                    object: { type: 'string' },
                    state: { type: 'string' },
                    animate: { type: 'boolean' },
                },
                required: ['object'],
            },
        },
        execute: (input, ctx) => Promise.resolve(executeSetGroupState(input, ctx)),
    },
    {
        tool: {
            name: 'wire_group_state',
            input_schema: {
                type: 'object' as const,
                properties: {
                    button: { type: 'string' },
                    target: { type: 'string' },
                    state: { type: 'string' },
                    mode: { type: 'string', enum: ['append', 'replace'] },
                },
                required: ['button', 'target'],
            },
        },
        execute: (input, ctx) => oneWiringAtATime(ctx, () => executeWireGroupState(input, ctx)),
    },
    sharedSceneTool('wire_interaction'),
    {
        tool: {
            name: 'simulate_select',
            input_schema: {
                type: 'object' as const,
                properties: {
                    object: { type: 'string' },
                    hold: { type: 'boolean' },
                },
                required: ['object'],
            },
        },
        execute: (input, ctx) => Promise.resolve(executeSimulateSelect(input, ctx)),
    },
    {
        tool: {
            name: 'inspect_bindings',
            input_schema: {
                type: 'object' as const,
                properties: { objects: { type: 'array', items: { type: 'string' }, minItems: 1, maxItems: 32 } },
                required: ['objects'],
            },
        },
        execute: async (input, ctx) => {
            if (!Array.isArray(input.objects) || !input.objects.length || input.objects.length > 32 || input.objects.some(name => typeof name !== 'string')) return 'Error: objects must contain 1 to 32 object names or paths';
            const objects = input.objects.map(name => resolveObject(name as string, ctx));
            if (objects.some(object => !object)) return 'Error: one or more objects could not be resolved';
            const diagnostics = inspectBindings([...new Set(objects.flatMap(object => object ? [object, ...object.getDescendants()] : []))]);
            const staticReadouts = findStaticReadouts(ctx.traceScene);
            return JSON.stringify({
                diagnostics, capped: diagnostics.length === 100,
                ...(staticReadouts.length ? { staticReadouts } : {}),
                scope: 'Authored fields on the requested objects and their descendants, including inactive states; not a whole-scene validity verdict.',
            });
        },
    },
    {
        tool: {
            name: 'probe_runtime',
            input_schema: {
                type: 'object' as const,
                properties: {
                    objects: { type: 'array', items: { type: 'string' }, minItems: 1, maxItems: 32 },
                    duration_ms: { type: 'number', minimum: 1, maximum: 10000 },
                    steps: {
                        type: 'array', minItems: 1, maxItems: 32,
                        items: {
                            type: 'object', additionalProperties: false,
                            properties: {
                                select: { type: 'string' },
                                duration_ms: { type: 'number', minimum: 1, maximum: 10000 },
                                expect: { type: 'array', maxItems: 32, items: {
                                    type: 'object', additionalProperties: false,
                                    properties: {
                                        object: { type: 'string' }, property: { type: 'string' },
                                        value: { type: ['number', 'string', 'boolean'] },
                                        tolerance: { type: 'number', minimum: 0 },
                                    }, required: ['object', 'property', 'value'],
                                } },
                            },
                        },
                    },
                },
                required: ['objects'],
            },
        },
        execute: async (input, ctx) => {
            try {
                const unknown = Object.keys(input).filter(key => !['objects', 'duration_ms', 'steps'].includes(key));
                if (unknown.length) return `Error: unknown field ${unknown.map(k => `"${k}"`).join(', ')}; probe_runtime takes objects, duration_ms (the observation time) and steps`;
                if (!Array.isArray(input.objects) || !input.objects.length || input.objects.length > 32 || input.objects.some(name => typeof name !== 'string')) return 'Error: objects must contain 1 to 32 object paths or names';
                const objects = input.objects.map(name => {
                    const object = resolveObject(name as string, ctx);
                    if (!object) throw new Error(`Object not found: ${String(name)}`);
                    return object;
                });
                if (input.steps !== undefined) {
                    return JSON.stringify(compactRuntimeReport(await probeRuntimeSequence(ctx.traceScene, [...new Set(objects)], input.steps,
                        name => resolveObject(name, ctx), name => executeSimulateSelect({ object: name }, ctx), input.duration_ms)));
                }
                const duration = input.duration_ms ?? 1000;
                if (typeof duration !== 'number') return 'Error: duration_ms must be a number';
                return JSON.stringify(compactRuntimeReport(await probeRuntime(ctx.traceScene, [...new Set(objects)], duration)));
            } catch (error) { return `Error: ${error instanceof Error ? error.message : String(error)}`; }
        },
    },
    {
        tool: {
            name: 'set_play_mode',
            input_schema: {
                type: 'object' as const,
                properties: {
                    enabled: { type: 'boolean' },
                },
                required: ['enabled'],
            },
        },
        execute: executeSetPlayMode,
    },
];
